import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ZoneChip, LageBadge, Stat, Spinner } from "../components/ui";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import { TrendingUp, ArrowDownRight, ArrowUpRight, Sparkles, AlertTriangle } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PORTAL_PLATFORMS } from "../lib/platforms";
import OnboardingChecklist from "../components/OnboardingChecklist";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [listings, setListings] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const { openChat } = useOutletContext() || {};

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, m, l, c] = await Promise.all([
        api.get("/stats"),
        api.get("/markets"),
        api.get("/listings?zone=GREEN,GREEN_BORDER&limit=8"),
        api.get("/cycle"),
      ]);
      setStats(s.data);
      setMarkets(m.data.markets);
      setListings(l.data.listings);
      setCycle(c.data);
    } catch (e) {
      console.error("Dashboard load failed", e);
      setErr(t("common.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err || !stats) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err || t("common.loadFailed")}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  const noData = stats.total_listings === 0;

  return (
    <div className="reveal">
      {/* PAGE HEADER */}
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-border bg-grid">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            {user?.name && (
              <div className="font-mono text-sm text-text-secondary mb-1">
                {t("dashboard.greeting", { name: user.name.split(" ")[0] })}
              </div>
            )}
            <div className="overline">{t("dashboard.eyebrow")}</div>
            <h1 className="mt-2 font-heading text-4xl md:text-5xl font-black tracking-tight">
              {t("dashboard.title")}
            </h1>
            <p className="mt-2 text-text-secondary text-sm max-w-xl">
              {t("dashboard.subtitle", { markets: markets.length, portals: PORTAL_PLATFORMS.length })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openChat}
              data-testid="dashboard-ai-cta"
              className="inline-flex items-center gap-2 border border-signal-green text-signal-green hover:bg-signal-green hover:text-black font-mono text-xs uppercase tracking-widest2 px-4 py-2 transition-colors"
            >
              <Sparkles size={14} /> {t("dashboard.askAi")}
            </button>
          </div>
        </div>
      </div>

      <OnboardingChecklist />

      {/* NO REAL DATA YET */}
      {noData && (
        <div className="px-6 md:px-10 py-4 border-b border-signal-yellow/30 bg-signal-yellow/10 flex items-start gap-3">
          <AlertTriangle size={16} className="text-signal-yellow flex-shrink-0 mt-0.5" />
          <div className="text-xs text-signal-yellow">
            <span className="font-bold">{t("dashboard.noDataBadge")}</span>{" "}
            <span className="text-text-secondary">
              {t("dashboard.noDataBody1")} <code className="text-white">push_to_web.py</code> {t("dashboard.noDataBody2")}{" "}
              <button onClick={() => nav("/diagnostics")} className="underline hover:text-white">{t("nav.diagnostics")}</button>.
            </span>
          </div>
        </div>
      )}

      {/* TOP STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-border" data-testid="dashboard-stats">
        <Stat label={t("auth.greenZone")} value={stats.green_zone_count} sub={t("dashboard.ofScreened", { total: stats.total_listings })} accent="green" testId="stat-green" />
        <Stat label={t("dashboard.yellowZone")} value={stats.yellow_zone_count} sub="Faktor 22 → 26" accent="yellow" />
        <Stat label={t("dashboard.redZone")} value={stats.red_zone_count} sub={t("dashboard.rejected")} accent="red" />
        <Stat label={t("dashboard.avgFaktor")} value={stats.avg_faktor} sub={t("dashboard.medPrice", { price: (stats.median_price/1000).toFixed(0) })} testId="stat-faktor" />
      </div>

      {/* MAIN 12-COL GRID */}
      <div className="grid grid-cols-12">
        {/* MARKET CYCLE — 7 col (hidden until cycle.py has run) */}
        {cycle?.available && (
          <section className="col-span-12 border-b border-border p-6">
            <div className="overline">{t("dashboard.marketCycle")}</div>
            <h3 className="mt-1 font-heading text-2xl font-bold">{t("dashboard.austrianHpi")}</h3>
            <div className="mt-1 flex items-end justify-between flex-wrap gap-2">
              <div>
                <span className="text-signal-green num text-sm tracking-widest2 flex items-center gap-1">
                  <TrendingUp size={14} /> {cycle.cycle.state}
                </span>
                <p className="mt-1 text-xs text-text-muted">{cycle.cycle.read}</p>
              </div>
              <div className="text-right">
                <div className="overline">{cycle.cycle.quarter}</div>
                <div className="num text-3xl text-white mt-1">{cycle.cycle.index}</div>
                <div className="num text-xs text-signal-green">+{cycle.cycle.yoy_pct}% YoY · +{cycle.cycle.qoq_pct}% QoQ</div>
              </div>
            </div>
            <div className="mt-6 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cycle.hpi_series} margin={{ left: -20, right: 10, top: 10 }}>
                  <XAxis dataKey="quarter" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#27272A" }} interval={7} />
                  <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#27272A" }} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }} labelStyle={{ color: "#A1A1AA" }} />
                  <Line type="linear" dataKey="index" stroke="#00FF66" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* TOP GREEN DEALS — 8 col */}
        <section className="col-span-12 lg:col-span-8 border-r border-b border-border p-6" data-testid="dashboard-top-deals">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="overline">{t("dashboard.livePipeline")}</div>
              <h3 className="mt-1 font-heading text-2xl font-bold">{t("dashboard.topGreenDeals")}</h3>
            </div>
            <button
              onClick={() => nav("/listings")}
              data-testid="dashboard-see-all-listings"
              className="font-mono text-[10px] uppercase tracking-widest2 text-signal-green hover:underline"
            >
              {t("dashboard.seeAll")}
            </button>
          </div>
          <DealTable rows={listings} onClick={(id) => nav(`/listings/${id}`)} t={t} />
        </section>

        {/* MARKETS BY GREEN COUNT — 4 col */}
        <section className="col-span-12 lg:col-span-4 border-b border-border p-6">
          <div className="overline">{t("dashboard.topMarkets")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("dashboard.allocator")}</h3>
          <div className="mt-5 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[...markets].sort((a, b) => b.green_count - a.green_count).slice(0, 8)}
                layout="vertical"
                margin={{ left: 0, right: 30, top: 10 }}
              >
                <XAxis type="number" stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis dataKey="code" type="category" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#27272A" }} width={60} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="green_count" fill="#00FF66">
                  {markets.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* MARKET HEATMAP — full width */}
        <section className="col-span-12 p-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="overline">{t("dashboard.compositeLage", { count: markets.length, unit: markets.length === 1 ? t("common.market") : t("common.markets") })}</div>
              <h3 className="mt-1 font-heading text-2xl font-bold">{t("dashboard.marketGrid")}</h3>
            </div>
            <button
              onClick={() => nav("/markets")}
              className="font-mono text-[10px] uppercase tracking-widest2 text-signal-green hover:underline"
            >
              {t("dashboard.openAllocator")}
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-px bg-border border border-border">
            {markets.map((m) => (
              <button
                key={m.code}
                onClick={() => nav(`/markets/${m.code}`)}
                data-testid={`market-tile-${m.code}`}
                className="bg-bg hover:bg-surfaceHover p-3 text-left transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <LageBadge grade={m.lage_grade} />
                  <span className="overline">{m.country}</span>
                </div>
                <div className="mt-2 text-xs font-mono text-text-secondary truncate">{m.name}</div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="num text-2xl font-light">{m.avg_faktor}</span>
                  <span className="overline">FAKTOR</span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-[10px] font-mono">
                  <span className="text-signal-green">{m.green_count}</span>
                  <span className="text-text-muted">/</span>
                  <span className="text-signal-yellow">{m.yellow_count}</span>
                  <span className="text-text-muted">/</span>
                  <span className="text-signal-red">{m.red_count}</span>
                  <span className="ml-auto text-text-muted flex items-center">
                    {m.pop_trend === "decline" ? <ArrowDownRight size={11} className="text-signal-red" /> : <ArrowUpRight size={11} className="text-signal-green" />}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function DealTable({ rows, onClick, t }) {
  return (
    <div className="border border-border">
      <div className="hidden md:grid md:grid-cols-12 text-[10px] font-mono uppercase tracking-widest2 text-text-muted border-b border-border bg-surface">
        <div className="col-span-1 p-3">{t("dashboard.colZone")}</div>
        <div className="col-span-3 p-3">{t("dashboard.colMarketAddress")}</div>
        <div className="col-span-1 p-3">{t("dashboard.colLage")}</div>
        <div className="col-span-1 p-3 text-right">{t("dashboard.colFaktor")}</div>
        <div className="col-span-2 p-3 text-right">{t("dashboard.colPrice")}</div>
        <div className="col-span-1 p-3 text-right">€/m²</div>
        <div className="col-span-1 p-3 text-right">{t("dashboard.colVsMarkt")}</div>
        <div className="col-span-1 p-3 text-right">{t("dashboard.colCond")}</div>
        <div className="col-span-1 p-3 text-right">{t("dashboard.colCashflow")}</div>
      </div>
      {rows.map((r) => (
        <button
          key={r.id}
          onClick={() => onClick(r.id)}
          data-testid={`deal-row-${r.id}`}
          className="block w-full text-left border-b border-border hover:bg-surfaceHover transition-colors md:grid md:grid-cols-12 md:items-center"
        >
          {/* Mobile: stacked card */}
          <div className="p-3 space-y-1.5 md:hidden">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ZoneChip zone={r.zone} />
                <LageBadge grade={r.lage_grade} />
              </div>
              <span className="num text-sm text-white">€{(r.price ?? 0).toLocaleString()}</span>
            </div>
            <div className="text-xs text-white">{r.market_name}</div>
            <div className="text-[10px] text-text-muted truncate">{r.address}</div>
            <div className="flex items-center justify-between text-[10px] font-mono text-text-muted">
              <span>Faktor <span className="num text-white">{(r.faktor ?? 0).toFixed(1)}</span></span>
              <span className={(r.vs_markt_pct ?? 0) < 0 ? "text-signal-green" : "text-signal-red"}>
                {r.vs_markt_pct != null ? `${r.vs_markt_pct > 0 ? "+" : ""}${r.vs_markt_pct}%` : "—"} {t("dashboard.vsMarktInline")}
              </span>
              <span className={(r.monthly_cashflow ?? 0) >= 0 ? "text-signal-green" : "text-signal-red"}>
                {r.monthly_cashflow != null ? `${r.monthly_cashflow >= 0 ? "+" : ""}€${r.monthly_cashflow}/mo` : "—"}
              </span>
            </div>
          </div>
          {/* Desktop: table row */}
          <div className="hidden md:contents">
            <div className="col-span-1 p-3"><ZoneChip zone={r.zone} /></div>
            <div className="col-span-3 p-3">
              <div className="text-xs text-white">{r.market_name}</div>
              <div className="text-[10px] text-text-muted truncate">{r.address}</div>
            </div>
            <div className="col-span-1 p-3"><LageBadge grade={r.lage_grade} /></div>
            <div className="col-span-1 p-3 num text-right text-white">{(r.faktor ?? 0).toFixed(1)}</div>
            <div className="col-span-2 p-3 num text-right text-white">€{(r.price ?? 0).toLocaleString()}</div>
            <div className="col-span-1 p-3 num text-right text-text-secondary">{(r.price_per_sqm ?? 0).toLocaleString()}</div>
            <div className={`col-span-1 p-3 num text-right ${(r.vs_markt_pct ?? 0) < 0 ? "text-signal-green" : "text-signal-red"}`}>
              {r.vs_markt_pct != null ? `${r.vs_markt_pct > 0 ? "+" : ""}${r.vs_markt_pct}%` : "—"}
            </div>
            <div className="col-span-1 p-3 num text-right text-text-secondary">{r.condition_score}</div>
            <div className={`col-span-1 p-3 num text-right ${(r.monthly_cashflow ?? 0) >= 0 ? "text-signal-green" : "text-signal-red"}`}>
              {r.monthly_cashflow != null ? `${r.monthly_cashflow >= 0 ? "+" : ""}€${r.monthly_cashflow}` : "—"}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
